import { SparklesIcon } from '@heroicons/react/24/outline';
import { useApolloClient } from '@apollo/client/react';
import classNames from 'classnames';
import { memo, useCallback } from 'react';
import { Button } from '../../../components/Button';
import { T, useT } from '../../../translation/src';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { AiErrorMessage } from './AiErrorMessage';
import { AiProgress } from './AiProgress';
import { aiAssistSessionFor, useAiAssistState, useSessionReferenceImage } from './aiAssistSession';
import { useAiRunState } from './aiProgressTracker';
import { AI_MAX_PROMPT_LENGTH } from './aiShared';
import { ReferenceImageField } from './ReferenceImageField';

/**
 * A smart object's left panel: describe a change (optionally with a
 * reference image), the AI rewrites the CURRENT draft and the result lands
 * as one undo step. The request, its live progress and the session's
 * changes live in the editor's AI session.
 */
const AiAssistPanel_: React.FC = () => {
  const { store, organisationId, ui } = useObjectEditor();
  const client = useApolloClient();
  const session = aiAssistSessionFor(store);
  const state = useAiAssistState(session);
  const run = useAiRunState(session.tracker);
  const reference = useSessionReferenceImage(session, state);
  const { spec } = useEditorState();
  const placeholders = {
    OBJECT: useT('e.g. add a second door and make the handles adjustable', { swc: true }),
    DOOR: useT('e.g. make it a double door with a 2/3 + 1/3 split', { swc: true }),
    WINDOW: useT('e.g. split the glass into three panes and add a sill inside', { swc: true }),
  };
  const placeholder = placeholders[spec.opening?.kind ?? 'OBJECT'];

  const locked = ui.preview !== null;
  const busy = state.running;
  const canApply = !locked && !busy && !state.imageReading && state.prompt.trim() !== '';
  const apply = useCallback(() => void session.apply(client, organisationId), [session, client, organisationId]);

  return (
    <div className="flex flex-col gap-5 px-4 py-3">
      <div className="flex flex-col gap-3">
        {locked && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <T _str="stop previewing the published version to change the draft" swc />
          </p>
        )}
        <label className="flex flex-col gap-1 text-sm">
          <T _str="describe a change" swc />
          <textarea
            value={state.prompt}
            onChange={(e) => session.setPrompt(e.target.value)}
            onPaste={reference.onPaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canApply) {
                e.preventDefault();
                apply();
              }
            }}
            placeholder={placeholder}
            maxLength={AI_MAX_PROMPT_LENGTH}
            disabled={locked || busy}
            rows={6}
            className={classNames(
              'w-full resize-y rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-black outline-none focus:border-french-violet-600',
              'disabled:opacity-50 dark:border-gray-600 dark:bg-[#262626] dark:text-white dark:focus:border-neon-green-300',
            )}
          />
        </label>
        <ReferenceImageField state={reference} disabled={locked || busy} />
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" size="xs" onClick={apply} disabled={!canApply}>
            <span className="flex items-center gap-1.5">
              <SparklesIcon className={classNames('h-4 w-4', busy && 'motion-safe:animate-pulse')} />
              {busy ? <T _str="designing…" swc /> : <T _str="apply" swc />}
            </span>
          </Button>
          {!busy && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              <T _str="Ctrl+Enter to apply" swc />
            </span>
          )}
        </div>
        <AiProgress run={run} />
        {state.error && <AiErrorMessage code={state.error} />}
      </div>
      <div className="flex flex-col gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <T _str="changes this session" />
        </div>
        {state.changes.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <T _str="each change is one step: undo it with Ctrl+Z" swc />
          </p>
        ) : (
          <>
            <ul className="flex flex-col gap-2">
              {state.changes.map((change) => (
                <li key={change.id} className="rounded-lg border border-gray-300 p-2 text-xs dark:border-gray-600">
                  <div className="mb-1 line-clamp-2 font-medium" title={change.prompt}>
                    {change.prompt}
                    {change.hadImage && (
                      <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                        (<T _str="with image" />)
                      </span>
                    )}
                  </div>
                  <div className="text-gray-600 dark:text-gray-300">{change.summary}</div>
                  {change.overwroteEdits && (
                    <div className="mt-1 text-gray-500 dark:text-gray-400">
                      <T _str="edits made while the change was designed were replaced: Ctrl+Z brings them back" swc />
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              <T _str="undo a change with Ctrl+Z" swc />
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export const AiAssistPanel = memo(AiAssistPanel_);
